/**
 * My Stack: /my-stack
 *
 * The reason an account is worth having. Before this, signing up unlocked a
 * file and then did nothing, so there was no reason to come back and the list
 * being built had no product behind it.
 *
 * Each saved tool shows how far through its path you are, and the answer that
 * put it there when it came from the quiz. Tools added straight from the
 * directory have no intent, so the "matched for" line is simply omitted
 * rather than filled with something invented.
 */

import { Link, useLocation } from "wouter";
import { BUTTON } from "@/lib/buttonStyles";
import { useEffect } from "react";
import { ChevronRight, ExternalLink, Trash2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { useStack } from "@/hooks/useStack";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/core/hooks/useAuth";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { tools, isPublished, PUBLISHED_TOOL_COUNT } from "@/lib/toolsData";
import { MEMBERSHIP, TOOL_PATHS } from "@/lib/ecosystemData";
import { findIntent } from "@/lib/quizData";

export default function MyStack() {
  const { isAuthenticated, loading } = useAuth();
  const { open } = useAuthModal();
  const [, setLocation] = useLocation();
  const stack = useStack();

  /* The apps they have paid for, read from the entitlements view via
     billing.myApps. Not fetched while signed out: the procedure answers empty
     for an anonymous caller anyway, and this page asks them to sign in first,
     so the request would be a round trip whose answer is already known. */
  const myApps = trpc.billing.myApps.useQuery(undefined, {
    enabled: !loading && isAuthenticated,
  });
  const apps = myApps.data?.apps ?? [];
  /* Whether they subscribe, not whether they own things. Somebody who bought
     all five apps outright owns exactly what a member owns; only one of them
     has a subscription to name, and only the other has an upgrade worth
     hearing about. See billing.myApps. */
  const isMember = myApps.data?.member ?? false;

  // Signed out, this page has nothing to show, so ask rather than render an
  // empty shell. Anything saved anonymously is adopted on sign-in.
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      open({
        mode: "login",
        title: "Sign in to see your stack",
        description:
          "Your saved tools and your progress are on your account. Anything you saved before signing in comes with you.",
      });
    }
  }, [loading, isAuthenticated, open]);

  // Array.from rather than spread: the tsconfig target predates
  // downlevel iteration of a Set.
  const saved = Array.from(stack.savedToolIds)
    .map(toolId => tools.find(t => t.id === toolId))
    // A retired tool leaves its row intact so history reads correctly, but it
    // should not render a card for something no longer in the directory.
    .filter((t): t is NonNullable<typeof t> => Boolean(t) && isPublished(t!));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader context="My Stack" />

      <main className="flex-1">
        <div className="container max-w-3xl py-8 sm:py-12">
          {/* Above the saved tools, deliberately.
              
              Someone who has bought an app and come back to find it should not
              have to scroll past a reading list to reach the thing they paid
              for. It is also the only part of this page that is not reachable
              some other way — every saved tool has a card in the directory,
              whereas an app they own is otherwise only findable if they still
              have the delivery email.

              Hidden entirely when they own nothing, rather than shown as an
              empty state advertising what they have not bought. My Stack is
              somewhere you keep your things; it is not a second pricing page. */}
          {apps.length > 0 ? (
            <section className="mb-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                Your apps
              </p>


              <h2 className="mt-3 font-['Fraunces'] text-2xl sm:text-3xl font-semibold text-foreground leading-tight tracking-tight">
                {apps.length === 1
                  ? "You have full access to this."
                  : "You have full access to these."}
              </h2>

              {/* Which of the two ways they got here, said plainly above the
                  list. A member should see their membership named — it is what
                  they pay for every month and the only place in the app that
                  acknowledges it. Somebody who bought one outright should see
                  what the membership would add, once, without the page turning
                  into a pitch.

                  Shown to any non-member who owns something, which includes
                  the person who has bought all five outright. That is not a
                  mistake: the line they see is about every app we build NEXT,
                  which they do not have. Checking their count against the
                  catalogue would cost a second round trip on this page to
                  refine a case that costs $1,235 to reach. */}
              {isMember ? (
                <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4 sm:p-5">
                  <p className="font-['Fraunces'] text-lg sm:text-xl font-semibold text-foreground">
                    Your Smart Stack + is active.
                  </p>
                </div>
              ) : (
                /* A card rather than one big link: it holds a button, and an
                   anchor inside an anchor is invalid. */
                <div className="mt-4 rounded-xl border border-border border-l-4 border-l-primary bg-card p-4 sm:p-5">
                  <p className="font-['Fraunces'] text-lg sm:text-xl font-semibold text-foreground">
                    Join {MEMBERSHIP.name} to get all of our Companion Apps
                    included
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <a
                      href={MEMBERSHIP.checkoutHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`shrink-0 ${BUTTON.primary}`}
                    >
                      Join Now
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <p className="text-base text-muted-foreground leading-relaxed">
                      Only {MEMBERSHIP.price}.
                    </p>
                  </div>
                </div>
              )}

              <ul className="mt-5 space-y-3">
                {apps.map(app => (
                  <li
                    key={app.key}
                    className="rounded-xl border border-border border-l-4 border-l-primary bg-card p-4 sm:p-5"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-['Fraunces'] text-xl font-semibold text-foreground">
                          {app.name}
                        </h3>
                        <p className="mt-1 text-base text-muted-foreground leading-relaxed">
                          Opens in a new tab. Signing in here signs you in
                          there, so you will not be asked again.
                        </p>
                      </div>
                      <a
                        href={app.origin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`shrink-0 ${BUTTON.primary}`}
                      >
                        Open {app.name}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Your stack
          </p>
          <h1 className="mt-3 font-['Fraunces'] text-3xl sm:text-4xl font-semibold text-foreground leading-tight tracking-tight">
            {saved.length === 0
              ? "Nothing saved yet."
              : `${saved.length} ${saved.length === 1 ? "tool" : "tools"}, and where you got to with each.`}
          </h1>

          {saved.length === 0 ? (
            <>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                Run the Roles Map and we&rsquo;ll match you to tools for the job
                you&rsquo;re actually doing, or add any tool straight from the
                directory with the plus button on its card.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  href="/"
                  className={BUTTON.primarySm}
                >
                  Run the Roles Map
                  <ChevronRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/"
                  className="inline-flex items-center justify-center min-h-11 px-5 rounded-md border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                >
                  Browse all {PUBLISHED_TOOL_COUNT} tools
                </Link>
              </div>
            </>
          ) : (
            <>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                Pick up wherever you left off, or run the Roles Map again for a
                different job.
              </p>

              <ul className="mt-6 space-y-3">
                {saved.map(tool => {
                  const path = TOOL_PATHS[tool.id];
                  const steps = path?.steps ?? [];
                  const live = steps.filter(s => s.state === "live");
                  const done = stack.countComplete(live.map(s => s.id));
                  const total = steps.length;
                  const pct = total ? Math.round((done / total) * 100) : 0;
                  const next = live.find(s => !stack.isStepComplete(s.id));
                  const intentId = stack.intentForTool(tool.id);
                  const intent = intentId ? findIntent(intentId) : undefined;

                  return (
                    <li
                      key={tool.id}
                      className="rounded-xl border border-border bg-card p-4 sm:p-5"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="min-w-0 flex-1">
                          <h2 className="font-['Fraunces'] text-xl font-semibold text-foreground">
                            {tool.emoji} {tool.name}
                          </h2>
                          {intent ? (
                            <p className="mt-0.5 text-sm text-muted-foreground">
                              Matched for: {intent.label.toLowerCase()}
                            </p>
                          ) : (
                            <p className="mt-0.5 text-sm text-muted-foreground">
                              Added from the directory
                            </p>
                          )}

                          {total > 0 ? (
                            <div className="mt-3 flex items-center gap-3 max-w-xs">
                              <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-primary transition-[width] duration-300"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-sm font-semibold text-primary whitespace-nowrap">
                                {done} of {total}
                              </span>
                            </div>
                          ) : null}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {tool.routeLink ? (
                            <Link
                              href={tool.routeLink}
                              className={BUTTON.primarySm}
                            >
                              {done === 0
                                ? "Start the path"
                                : next
                                  ? `Continue step ${steps.indexOf(next) + 1}`
                                  : "Review"}
                            </Link>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => stack.removeTool(tool.id)}
                            aria-label={`Remove ${tool.name} from My Stack`}
                            title={`Remove ${tool.name} from My Stack`}
                            className="inline-flex items-center justify-center h-11 w-11 rounded-md border border-border text-muted-foreground hover:border-destructive hover:text-destructive transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <section className="mt-10 rounded-xl border border-border border-l-4 border-l-primary bg-card p-6">
                <h2 className="font-['Fraunces'] text-xl font-semibold text-foreground">
                  Need a tool for something else?
                </h2>
                <p className="mt-2 text-base text-muted-foreground leading-relaxed">
                  The Roles Map takes two questions. Whatever it matches gets
                  added here.
                </p>
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setLocation("/")}
                    className={BUTTON.primarySm}
                  >
                    Run the Roles Map again
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </section>
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
